import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { generateSocialPost, SocialPostInsufficientCreditsError } from '@/api/generateSocialPost';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import type { SocialPostFormData, SocialPostResult } from '@/pages/social-post/types';
import {
  CONTENT_STYLE_OPTIONS,
  POST_TYPES,
  SOCIAL_PLATFORMS,
  SOCIAL_POST_COST,
} from '@/pages/social-post/types';
import SocialPostResults from '@/pages/social-post/components/SocialPostResults';

const anonymousGenerationKey = 'adforge_social_quick_start_anonymous_generations';
const anonymousGenerationLimit = 3;

function makeDefaultForm(idea = ''): SocialPostFormData {
  return {
    identityType: 'business',
    businessName: '',
    creatorNiche: '',
    creatorPersonality: '',
    contentPillars: '',
    creatorAudienceType: '',
    creatorTone: '',
    contentStyle: 'community-engagement',
    platform: 'Instagram',
    postType: 'Daily Promo',
    goal: 'Create an organic post that gets attention and starts a conversation',
    offerTopic: idea,
    targetAudience: '',
    targetAudienceType: '',
    brandVoice: 'Friendly',
    imageStyle: '',
    creativeDirection: '',
    captionLength: 'Medium',
    includeImage: false,
    cta: '',
    hashtags: '',
    specialInstructions: '',
    autoMode: false,
    referenceImageBase64: null,
    referenceImagePrompt: '',
    theme: '',
    eventPromotion: '',
    referenceStyle: '',
  };
}

export default function SocialQuickStartPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const starterIdea = searchParams.get('idea')?.trim() ?? '';
  const [formData, setFormData] = useState<SocialPostFormData>(() => makeDefaultForm(starterIdea));
  const [result, setResult] = useState<SocialPostResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [anonymousGenerations, setAnonymousGenerations] = useState(() => {
    if (typeof window === 'undefined') return 0;
    return Number(window.localStorage.getItem(anonymousGenerationKey) ?? '0') || 0;
  });
  const generationAbortRef = useRef<AbortController | null>(null);

  const identityLabel = formData.identityType === 'creator' ? 'Creator / brand name' : 'Business / brand name';
  const canGenerate = formData.businessName.trim().length > 1 && formData.offerTopic.trim().length > 2 && formData.platform;
  const anonymousLimitReached = (!user || user.is_anonymous) && anonymousGenerations >= anonymousGenerationLimit;
  const missingRequiredFields = [
    formData.businessName.trim().length > 1 ? '' : 'name',
    formData.offerTopic.trim().length > 2 ? '' : 'topic',
    formData.platform ? '' : 'platform',
  ].filter(Boolean);

  const selectedStyleLabel = useMemo(
    () => CONTENT_STYLE_OPTIONS.find((item) => item.id === formData.contentStyle)?.label ?? 'Community Engagement',
    [formData.contentStyle]
  );

  const updateForm = (patch: Partial<SocialPostFormData>) => {
    setFormData((current) => ({ ...current, ...patch }));
  };

  const ensureAnonymousSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return;

    const { data, error: anonError } = await supabase.auth.signInAnonymously();
    if (anonError || !data.user) {
      throw new Error('Start a free account to generate your first social post.');
    }

    await supabase
      .from('profiles')
      .upsert(
        { id: data.user.id, email: data.user.email ?? '', plan: 'free' },
        { onConflict: 'id', ignoreDuplicates: true }
      );
  };

  const handleGenerate = async () => {
    if (!canGenerate || isGenerating) return;
    if (anonymousLimitReached) {
      setError('Create a free account to keep generating and save your social posts.');
      return;
    }

    const abortController = new AbortController();
    generationAbortRef.current = abortController;
    setIsGenerating(true);
    setError('');
    setResult(null);

    try {
      await ensureAnonymousSession();
      const generated = await generateSocialPost(formData, user?.id, undefined, undefined, undefined, {
        signal: abortController.signal,
      });
      if (abortController.signal.aborted || generationAbortRef.current !== abortController) return;
      setResult(generated);
      if (!user || user.is_anonymous) {
        const nextCount = anonymousGenerations + 1;
        setAnonymousGenerations(nextCount);
        window.localStorage.setItem(anonymousGenerationKey, String(nextCount));
      }
    } catch (err) {
      if (abortController.signal.aborted) {
        setError('Generation stopped. Update your form and try again when you are ready.');
      } else if (err instanceof SocialPostInsufficientCreditsError) {
        setError('You need more credits before generating another social post.');
      } else {
        setError(err instanceof Error ? err.message : 'Generation failed. Please try again.');
      }
    } finally {
      if (generationAbortRef.current === abortController) {
        generationAbortRef.current = null;
        setIsGenerating(false);
      }
    }
  };

  const stopGeneration = () => {
    generationAbortRef.current?.abort();
    generationAbortRef.current = null;
    setIsGenerating(false);
    setError('Generation stopped. Update your form and try again when you are ready.');
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-sm font-extrabold text-stone-900">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-400 text-white">
              <i className="ri-sparkling-2-fill" />
            </span>
            AdForge AI
          </Link>
          <Link to={user && !user.is_anonymous ? '/dashboard/social-post' : '/signup'} className="text-sm font-semibold text-stone-500 hover:text-stone-900">
            {user && !user.is_anonymous ? 'Full social studio' : 'Save my post'}
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-[0.88fr_1.12fr]">
        <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-wider text-sky-500">Social quick start</p>
            <h1 className="mt-1 text-2xl font-extrabold text-stone-950">Generate your first social post</h1>
            <p className="mt-2 text-sm leading-6 text-stone-500">
              Start with a simple topic. Required fields are marked.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                Identity
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'business' as const, label: 'Business' },
                  { id: 'creator' as const, label: 'Creator' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => updateForm({ identityType: item.id })}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-bold transition ${
                      formData.identityType === item.id
                        ? 'border-sky-400 bg-sky-50 text-sky-700'
                        : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-sky-300'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                {identityLabel}
                <span className="text-sky-500">*</span>
              </label>
              <input
                value={formData.businessName}
                onChange={(event) => updateForm({ businessName: event.target.value })}
                placeholder="e.g. Dino Nuggets"
                required
                aria-required="true"
                className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:bg-white"
              />
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                Topic
                <span className="text-sky-500">*</span>
              </label>
              <textarea
                value={formData.offerTopic}
                onChange={(event) => updateForm({ offerTopic: event.target.value })}
                placeholder="e.g. Announce our new lunch special in a funny, local voice"
                required
                aria-required="true"
                rows={3}
                className="w-full resize-none rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:bg-white"
              />
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                Platform
                <span className="text-sky-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SOCIAL_PLATFORMS.slice(0, 5).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => updateForm({ platform: item.id })}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-bold transition ${
                      formData.platform === item.id
                        ? 'border-sky-400 bg-sky-50 text-sky-700'
                        : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-sky-300'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-stone-600">Post type</label>
                <select
                  value={formData.postType}
                  onChange={(event) => updateForm({ postType: event.target.value })}
                  className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm font-semibold outline-none transition focus:border-sky-400 focus:bg-white"
                >
                  {POST_TYPES.slice(0, 12).map((item) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-stone-600">Style</label>
                <select
                  value={formData.contentStyle}
                  onChange={(event) => updateForm({ contentStyle: event.target.value })}
                  className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm font-semibold outline-none transition focus:border-sky-400 focus:bg-white"
                >
                  {CONTENT_STYLE_OPTIONS.map((item) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <div className="flex items-start gap-2">
              <i className="ri-information-line mt-0.5" />
              <p>
                Quick Start creates caption copy only for speed. The full Social Studio adds images, history, daily content, presets, and deeper controls.
              </p>
            </div>
          </div>

          {anonymousLimitReached && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              You used your {anonymousGenerationLimit} quick-start posts. Create a free account to keep generating and save your work.
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleGenerate}
            disabled={!canGenerate || isGenerating || anonymousLimitReached}
            className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-4 text-sm font-extrabold transition ${
              canGenerate && !isGenerating && !anonymousLimitReached
                ? 'bg-gradient-to-r from-sky-400 to-cyan-300 text-slate-950 hover:from-sky-300 hover:to-cyan-200'
                : 'bg-stone-200 text-stone-400'
            }`}
          >
            {isGenerating ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" />
                Generating post...
              </>
            ) : (
              <>
                <i className="ri-sparkling-2-fill text-base" />
                Generate Social Post - ~{SOCIAL_POST_COST} Credits
              </>
            )}
          </button>
          {!canGenerate && !isGenerating && (
            <p className="mt-2 text-center text-xs font-semibold text-stone-500">
              Add {missingRequiredFields.join(', ')} to enable generation.
            </p>
          )}
          {isGenerating && (
            <button
              type="button"
              onClick={stopGeneration}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-stone-300 bg-white py-3 text-sm font-extrabold text-stone-700 transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
            >
              <i className="ri-stop-circle-line text-base" />
              Stop and edit form
            </button>
          )}
        </section>

        <section className="min-h-[620px]">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-stone-400">Preview</p>
              <h2 className="text-lg font-extrabold text-stone-950">
                {result ? `${formData.platform} post` : `${selectedStyleLabel} direction`}
              </h2>
            </div>
            <Link
              to="/dashboard/social-post"
              className="hidden rounded-xl border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-500 hover:border-sky-300 hover:text-sky-700 sm:inline-flex"
            >
              Open full studio
            </Link>
          </div>
          <SocialPostResults
            result={result}
            isGenerating={isGenerating}
            platform={formData.platform}
            businessName={formData.businessName || 'Your brand'}
            imageStyle={formData.imageStyle}
            onRegenerate={handleGenerate}
          />
        </section>
      </main>
    </div>
  );
}
